/**
 * Weekly Agenda Manager
 *
 * Static utility for weekly agenda date math, formatting, and event grouping.
 * Computes week ranges, header labels, and groups events by (day, member).
 *
 * Follows single responsibility: weekly agenda business logic only.
 */

import { LocalDateFormatter } from '@/lib/formatters/LocalDateFormatter';
import { FamilyResourceMapper } from '@/lib/family/FamilyResourceMapper';
import type { CalendarEvent } from '@/types/events';
import type { FamilyMember } from '@/types/family-member';
import type { CalendarMemberMapping } from '@/types/calendar-member-mapping';
import type {
  WeeklyAgendaCellData,
  WeeklyAgendaDayLabel,
  WeeklyAgendaDayRowData,
  WeeklyAgendaWeekData,
} from '@/types/weekly-agenda';

const DAY_LABELS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

const ORDINAL_SUFFIXES: Record<number, string> = {
  1: 'st', 2: 'nd', 3: 'rd',
  21: 'st', 22: 'nd', 23: 'rd',
  31: 'st',
};

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export class WeeklyAgendaManager {
  /**
   * Get the Monday of the week containing the given date.
   */
  static getMondayOfWeek(date: Date): Date {
    const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const dayOfWeek = result.getDay(); // 0=Sun, 1=Mon, ...
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    result.setDate(result.getDate() + diff);
    return result;
  }

  /**
   * Get the Monday of the current week.
   */
  static getCurrentWeekMonday(): Date {
    return this.getMondayOfWeek(new Date());
  }

  /**
   * Add days to a date, returning a new Date.
   */
  static addDays(date: Date, days: number): Date {
    const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    result.setDate(result.getDate() + days);
    return result;
  }

  /**
   * Get the Sunday (end) of a week given its Monday (start).
   */
  static getSundayOfWeek(monday: Date): Date {
    return this.addDays(monday, 6);
  }

  /**
   * Format ordinal suffix for a day number (e.g. 1st, 2nd, 3rd, 4th).
   */
  static formatOrdinal(day: number): string {
    const suffix = ORDINAL_SUFFIXES[day] ?? 'th';
    return `${day}${suffix}`;
  }

  /**
   * Format the week header label, e.g. "30th Mar - 5th Apr".
   * If both dates are in the same month, omits the first month.
   */
  static formatWeekHeaderLabel(monday: Date): string {
    const sunday = this.getSundayOfWeek(monday);
    const startDay = this.formatOrdinal(monday.getDate());
    const endDay = this.formatOrdinal(sunday.getDate());
    const startMonth = MONTH_NAMES[monday.getMonth()];
    const endMonth = MONTH_NAMES[sunday.getMonth()];

    if (monday.getMonth() === sunday.getMonth()) {
      return `${startDay} - ${endDay} ${endMonth}`;
    }
    return `${startDay} ${startMonth} - ${endDay} ${endMonth}`;
  }

  /**
   * Format day label as structured object for stacked display.
   */
  static formatDayLabel(date: Date, dayIndex: number): WeeklyAgendaDayLabel {
    return {
      dayAbbrev: DAY_LABELS[dayIndex],
      dateNum: String(date.getDate()),
      monthAbbrev: MONTH_NAMES[date.getMonth()].toUpperCase(),
    };
  }

  /**
   * Build the full week data structure for rendering.
   */
  static buildWeekData(
    monday: Date,
    allEvents: CalendarEvent[],
    familyMembers: FamilyMember[],
    calendarMappings: CalendarMemberMapping[],
    meMemberId: string
  ): WeeklyAgendaWeekData {
    const todayKey = LocalDateFormatter.getTodayDateString();
    const days: WeeklyAgendaDayRowData[] = [];

    for (let i = 0; i < 7; i++) {
      const dayDate = this.addDays(monday, i);
      const dateKey = LocalDateFormatter.toDateKey(dayDate);
      const dayEvents = FamilyResourceMapper.filterEventsForDate(allEvents, dateKey);
      const cells = this.groupEventsByMember(
        dayEvents, familyMembers, calendarMappings, meMemberId
      );

      days.push({
        dateKey,
        dayLabel: this.formatDayLabel(dayDate, i),
        isToday: dateKey === todayKey,
        cells,
      });
    }

    return {
      weekStartDate: LocalDateFormatter.toDateKey(monday),
      headerLabel: this.formatWeekHeaderLabel(monday),
      days,
    };
  }

  /**
   * Group events for a single day into cells per family member.
   * Maintains the same member ordering as the input familyMembers array.
   */
  static groupEventsByMember(
    dayEvents: CalendarEvent[],
    familyMembers: FamilyMember[],
    calendarMappings: CalendarMemberMapping[],
    meMemberId: string
  ): WeeklyAgendaCellData[] {
    const memberEventsMap = new Map<string, CalendarEvent[]>();

    // Initialize empty arrays for all members
    for (const member of familyMembers) {
      memberEventsMap.set(member.id, []);
    }

    // Assign each event to its resolved members
    for (const event of dayEvents) {
      const memberIds = FamilyResourceMapper.resolveEventMembers(
        event, calendarMappings, meMemberId
      );
      for (const memberId of memberIds) {
        const existing = memberEventsMap.get(memberId);
        if (existing) {
          existing.push(event);
        }
      }
    }

    // Sort each member's events by start time
    for (const events of memberEventsMap.values()) {
      events.sort((a, b) => {
        const aTime = a.startTime ?? '00:00';
        const bTime = b.startTime ?? '00:00';
        return aTime.localeCompare(bTime);
      });
    }

    // Build cells in family member order
    return familyMembers.map(member => ({
      memberId: member.id,
      events: memberEventsMap.get(member.id) ?? [],
    }));
  }

  /**
   * Get the date range (Monday to Sunday) as YYYY-MM-DD strings.
   */
  static getWeekDateRange(monday: Date): { from: string; to: string } {
    const sunday = this.getSundayOfWeek(monday);
    return {
      from: LocalDateFormatter.toDateKey(monday),
      to: LocalDateFormatter.toDateKey(sunday),
    };
  }
}
